'use client'

import Image from 'next/image'
import Link from 'next/link'
import React, { useEffect, useState } from 'react'
import { LuArrowUpLeft, LuArrowUpRight } from 'react-icons/lu'

import type { Footer as FooterType } from '@/payload-types'

import { NewsletterSubscribeModal } from '@/components/frontend/NewsletterSubscribeModal'
import type { ExperienceStats } from '@/components/frontend/RedStatisticsBand'
import { RedStatisticsBand } from '@/components/frontend/RedStatisticsBand'
import { useLocale } from '@/components/LocaleContext'
import { DEFAULT_FOOTER_ADDRESS } from '@/utilities/contactDefaults'

const FOOTER_RED = '#FF0000'
const FOOTER_DARK = '#1a1a1a'
const STORAGE_KEY = 'gallium_contact_email'

const defaultQuickLinks1 = [
  { label: 'About Us', url: '/about' },
  { label: 'Pricing', url: '/products' },
  { label: 'Blog', url: '/insights' },
  { label: 'Contact us', url: '/contact' },
]

type FooterProps = {
  footer?: FooterType | null
  logoUrl?: string | null
  experienceStats?: ExperienceStats | null
  /** When false, the red statistics band is omitted (e.g. on home it appears above Blog). */
  showStatisticsBand?: boolean
}

export function Footer({
  footer,
  logoUrl,
  experienceStats,
  showStatisticsBand = true,
}: FooterProps) {
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [newsletterModalOpen, setNewsletterModalOpen] = useState(false)

  const { locale, translatedData } = useLocale()

  useEffect(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null
    if (stored) setNewsletterEmail(stored)
  }, [])

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setNewsletterEmail(value)
    if (typeof window !== 'undefined') {
      if (value.trim()) localStorage.setItem(STORAGE_KEY, value.trim())
      else localStorage.removeItem(STORAGE_KEY)
    }
  }

  const linkColumns = footer?.links ?? []
  const contact = footer?.contact
  const col1 = linkColumns[0]
  const links1 = (col1?.items?.length ? col1.items : defaultQuickLinks1).map((i) => {
    const key = i.label?.toLowerCase()
    return {
      ...i,
      label: translatedData?.navbar[key as keyof typeof translatedData.navbar]
        ? translatedData?.navbar[key as keyof typeof translatedData.navbar]
        : translatedData?.common[key as keyof typeof translatedData.common]
          ? translatedData?.common[key as keyof typeof translatedData.common]
          : i.label,
    }
  })
  const socials =
    footer?.socials?.filter((s) => s.platform?.trim() && s.url?.trim()) ?? []
  const phone = contact?.phone ?? '+44 79 3690 5802'
  const email = contact?.email ?? 'asfilter@eim.ae'
  const address = contact?.address ?? DEFAULT_FOOTER_ADDRESS[locale]
  const mapsUrl =
    'https://www.google.com/maps?q=Industrial+Area+3,+Industrial+Area,+Sharjah,+United+Arab+Emirates'
  const gmailComposeUrl = `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(email.trim())}`

  return (
    <footer className="relative z-30 w-full" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <NewsletterSubscribeModal
        isOpen={newsletterModalOpen}
        onClose={() => setNewsletterModalOpen(false)}
        initialEmail={newsletterEmail}
      />
      {showStatisticsBand && <RedStatisticsBand experienceStats={experienceStats} />}

      {/* Bottom dark section – same left/right margin as header */}
      <div
        className="px-4 max-[400px]:px-3 py-12 text-white sm:px-10 lg:px-[141px]"
        style={{ backgroundColor: FOOTER_DARK }}
      >
        <div className="mx-auto grid max-w-[1638px] gap-10 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {/* Column 1: Logo + Newsletter */}
          <div className="space-y-6">
            <div>
              <Link href="/" className="inline-block" aria-label="Gallium home">
                {logoUrl ? (
                  <Image
                    src={logoUrl}
                    alt="Gallium – Inspired by filtration"
                    width={140}
                    height={36}
                    className="h-9 w-auto object-contain object-left"
                  />
                ) : (
                  <span className="text-2xl font-bold" dir="ltr" style={{ color: FOOTER_RED }}>
                    GALLIUM®
                  </span>
                )}
              </Link>
            </div>
            <div className="space-y-6">
              <h3
                className="align-middle text-white rtl:text-right"
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '22.7px',
                  lineHeight: '24px',
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                }}
              >
                {translatedData?.common.newsletter_signup}
              </h3>
              <div className="flex flex-col gap-6">
                <input
                  type="email"
                  name="email"
                  placeholder={translatedData?.common.email_address ?? 'Email address'}
                  value={newsletterEmail}
                  onChange={handleEmailChange}
                  className="hidden w-full border-b border-neutral-500 bg-transparent px-0 text-left text-white placeholder:text-left placeholder:text-neutral-500 focus:border-white focus:outline-none"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '16px',
                    lineHeight: '100%',
                    letterSpacing: 0,
                    verticalAlign: 'middle',
                    height: '23px',
                    opacity: 1,
                  }}
                />
                <div className="view-projects-btn-wrapper inline-flex w-[218px] justify-start max-[480px]:w-full max-[480px]:min-w-0 self-start">
                  <button
                    type="button"
                    onClick={() => setNewsletterModalOpen(true)}
                    className="view-projects-btn inline-flex w-full items-center justify-center gap-2.5 min-h-[66px] h-[66px] bg-[#E60023] text-white px-[33px] py-[30px] text-xs font-medium uppercase tracking-wider focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#E60023] max-[480px]:w-full max-[480px]:min-w-0"
                  >
                    {translatedData?.common.subscribe_us}
                    {locale === 'ar' ? (
                      <LuArrowUpLeft
                        size={18}
                        aria-hidden
                        className="shrink-0 view-projects-btn__arrow"
                      />
                    ) : (
                      <LuArrowUpRight
                        size={18}
                        aria-hidden
                        className="shrink-0 view-projects-btn__arrow"
                      />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Column 2: Quick Links + Social Media */}
          <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 sm:items-start sm:gap-x-5 md:gap-x-10 lg:gap-x-16 xl:gap-x-[120px]">
            <div className="min-w-0">
              <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-white">
                {translatedData?.common.quick_links}
              </h3>
              <ul className="space-y-2">
                {links1.map((item) => (
                  <li key={item.label}>
                    <Link
                      href={item.url}
                      className="align-middle text-white/90 transition-colors hover:text-white"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 400,
                        fontStyle: 'normal',
                        fontSize: '16px',
                        lineHeight: '24px',
                        letterSpacing: 0,
                        verticalAlign: 'middle',
                      }}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            {socials.length > 0 && (
              <div className="min-w-0">
                <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-white">
                  {translatedData?.common.follow_us ?? 'Follow Us'}
                </h3>
                <ul className="space-y-2">
                  {socials.map((item) => (
                    <li key={item.id ?? item.platform}>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="align-middle text-white/90 transition-colors hover:text-white"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: 400,
                          fontStyle: 'normal',
                          fontSize: '16px',
                          lineHeight: '24px',
                          letterSpacing: 0,
                          verticalAlign: 'middle',
                        }}
                      >
                        {item.platform}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Column 3: Contact */}
          <div className="space-y-4 text-left rtl:text-right text-sm" >
            <a
            dir="ltr"
              href={`tel:${phone.replace(/\s/g, '')}`}
              className="block w-fit border-b transition-opacity hover:opacity-90"
              style={{
                color: FOOTER_RED,
                borderColor: FOOTER_RED,
                fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '23.4px',
                lineHeight: '24px',
                letterSpacing: 0,
                verticalAlign: 'middle',
                textTransform: 'capitalize',
              }}
            >
              {phone}
            </a>
            {/* Mobile: mailto opens default mail app (e.g. Gmail app) */}
            <a
              href={`mailto:${email.trim()}`}
              dir="ltr"
              className="block font-bold uppercase text-white transition-colors hover:text-white/90 md:hidden"
            >
              {email}
            </a>
            {/* Desktop: Gmail in new tab */}
            <a
              href={gmailComposeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden font-bold uppercase text-white transition-colors hover:text-white/90 md:block"
            >
              {email}
            </a>
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex gap-2 transition-opacity hover:opacity-90"
            >
              <Image
                src="/icon/locationicon.svg"
                alt="location"
                width={20}
                height={20}
                className="mt-0.5 shrink-0 self-start"
                aria-hidden
              />
              <span
                className="whitespace-pre-line align-middle text-white/90"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 400,
                  fontStyle: 'normal',
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: 0,
                  verticalAlign: 'middle',
                }}
              >
                {address}
              </span>
            </a>
          </div>
        </div>

        {/* Bottom bar: divider + legal links */}
        <div className="mx-auto mt-10 max-w-[1638px] border-t border-neutral-700 pt-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <span className="text-xs text-white/60" suppressHydrationWarning>
              ©Copyright {new Date().getFullYear()},{' '}
              {translatedData?.common.all_rights_reserved ?? 'All Rights Reserved'}
            </span>
            <div className="flex gap-6 text-xs text-white/90">
              <Link href="/privacy-policy" className="transition-colors hover:text-white">
                {translatedData?.common.privacy_policy ?? 'Privacy Policy'}
              </Link>
              <Link href="/terms-and-conditions" className="transition-colors hover:text-white">
                {translatedData?.common.terms_and_conditions ?? 'Terms & Conditions'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
